import { useEffect, useState } from 'react'

export function todayKey(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const DAY_ROLLOVER_EVENT = 'gps-day-rollover'

let rolloverWatcherStarted = false
let lastSeenDay = ''

function startRolloverWatcher() {
  if (rolloverWatcherStarted || typeof window === 'undefined') return
  rolloverWatcherStarted = true
  lastSeenDay = todayKey()
  window.setInterval(() => {
    const now = todayKey()
    if (now !== lastSeenDay) {
      lastSeenDay = now
      window.dispatchEvent(new CustomEvent(DAY_ROLLOVER_EVENT))
    }
  }, 30_000) // check every 30s — cheap and snappy at midnight
}

/**
 * Reactive hook that returns today's date key (YYYY-MM-DD).
 * Re-renders the calling component whenever the calendar day changes
 * while the app is open.
 */
export function useTodayKey(): string {
  const [key, setKey] = useState(() => todayKey())

  useEffect(() => {
    startRolloverWatcher()

    const refresh = () => setKey(todayKey())
    window.addEventListener(DAY_ROLLOVER_EVENT, refresh)

    // Belt-and-suspenders: also check on focus/visibility (e.g. wake from sleep)
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        const now = todayKey()
        if (now !== lastSeenDay) {
          lastSeenDay = now
          window.dispatchEvent(new CustomEvent(DAY_ROLLOVER_EVENT))
        }
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)

    return () => {
      window.removeEventListener(DAY_ROLLOVER_EVENT, refresh)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
  }, [])

  return key
}
